import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useProject } from "../context/ProjectContext";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Package, Users, TrendingUp, Zap, Share2, FileText, Video, Newspaper, Image as ImageIcon, Plus, Archive, FolderKanban } from "lucide-react";

const EUR = (v) => new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(v || 0);

export default function AlleProjekte() {
  const { switchProject, refresh } = useProject();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState({});

  const load = async () => {
    const { data } = await api.get("/overview/metrics");
    setData(data);
    try { await refresh(); } catch { /* ignore */ }
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const open = async (pid) => {
    await switchProject(pid);
    nav("/");
  };

  const archive = async (pid) => {
    setBusy({ ...busy, [pid]: true });
    try { await api.post(`/projects/${pid}/archive`); await load(); } finally { setBusy({ ...busy, [pid]: false }); }
  };
  const unarchive = async (pid) => {
    setBusy({ ...busy, [pid]: true });
    try { await api.post(`/projects/${pid}/unarchive`); await load(); } finally { setBusy({ ...busy, [pid]: false }); }
  };

  if (!data) return <div className="p-8 text-slate-400" data-testid="overview-loading">Lade Übersicht…</div>;
  const t = data.totals;
  // Single source of truth: /overview/metrics returns BOTH active and archived (all live, non-deleted) projects.
  const activeCards = (data.projects || []).filter((c) => c.project.status !== "archived");
  const archivedCards = (data.projects || []).filter((c) => c.project.status === "archived");

  return (
    <div className="p-6 lg:p-10 max-w-[1400px] mx-auto" data-testid="alle-projekte-page">
      <header className="mb-8 flex items-start justify-between gap-4">
        <div>
          <div className="text-xs font-semibold uppercase tracking-wider text-indigo-400">Alle Projekte</div>
          <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight mt-1 text-white">Globale Übersicht</h1>
          <p className="text-slate-400 text-sm mt-2">Aggregierte Metriken über alle aktiven Projekte.</p>
        </div>
      </header>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <Stat label="Projekte" value={t.projects} icon={FolderKanban} tint="indigo" testid="overview-total-projects" />
        <Stat label="Produkte" value={t.products} icon={Package} tint="emerald" testid="overview-total-products" />
        <Stat label="Leads" value={t.leads} icon={Users} tint="amber" testid="overview-total-leads" />
        <Stat label="Deals (gesamt)" value={t.deals} icon={TrendingUp} tint="violet" testid="overview-total-deals" />
        <Stat label="Pipeline offen" value={EUR(t.pipeline_value_open)} icon={TrendingUp} tint="amber" testid="overview-pipeline-open" />
        <Stat label="Gewonnen" value={EUR(t.won_value)} icon={TrendingUp} tint="emerald" testid="overview-won" />
        <Stat label="Aktive Skills" value={t.active_skills} icon={Zap} tint="emerald" testid="overview-skills" />
        <Stat label="Berichte" value={t.research_reports} icon={Newspaper} tint="indigo" testid="overview-reports" />
      </div>

      <h2 className="text-sm font-semibold tracking-wider uppercase text-slate-400 mb-3">Pro Projekt</h2>
      {activeCards.length === 0 && (
        <Card className="bg-[#111827] border-white/5 text-slate-100" data-testid="overview-empty">
          <CardContent className="p-10 text-center">
            <div className="text-6xl mb-3">🚀</div>
            <h3 className="text-lg font-bold text-white">Lege dein erstes Projekt an</h3>
            <p className="text-sm text-slate-400 mt-1 mb-5">Ein Projekt bündelt alles für eine Marke oder einen Kunden.</p>
            <Button className="bg-emerald-500 hover:bg-emerald-400 text-slate-900 font-semibold" onClick={() => document.querySelector('[data-testid="project-switcher-trigger"]')?.click()}>
              <Plus className="h-4 w-4 mr-2" /> Projekt anlegen
            </Button>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {activeCards.map((card) => (
          <Card
            key={card.project.id}
            className="bg-[#111827] border-white/5 text-slate-100 hover:border-emerald-500/30 transition cursor-pointer"
            onClick={() => open(card.project.id)}
            data-testid={`project-card-${card.project.id}`}
          >
            <CardHeader className="flex-row items-start justify-between gap-3 pb-2">
              <div className="flex items-center gap-3 min-w-0">
                <span className="h-10 w-10 rounded-xl grid place-items-center text-lg shrink-0"
                      style={{ background: (card.project.color || "#10b981") + "22", color: card.project.color || "#10b981" }}>
                  {card.project.emoji || "·"}
                </span>
                <div className="min-w-0">
                  <CardTitle className="text-base font-bold truncate">{card.project.name}</CardTitle>
                  <div className="text-[11px] text-slate-500 truncate">{card.project.description || "—"}</div>
                </div>
              </div>
              <Button size="sm" variant="ghost" className="h-7 text-xs text-slate-400"
                onClick={(e) => { e.stopPropagation(); archive(card.project.id); }}
                disabled={busy[card.project.id]} data-testid={`project-archive-${card.project.id}`}>
                <Archive className="h-3.5 w-3.5" />
              </Button>
            </CardHeader>
            <CardContent className="pt-0">
              <div className="grid grid-cols-3 gap-2 text-xs text-slate-400">
                <Mini icon={Package} label="Produkte" value={card.products} />
                <Mini icon={Users} label="Leads" value={card.leads} />
                <Mini icon={TrendingUp} label="Pipeline" value={EUR(card.pipeline_value_open)} />
                <Mini icon={Zap} label="Skills" value={card.active_skills} />
                <Mini icon={Share2} label="Social" value={card.social_posts} />
                <Mini icon={FileText} label="Seiten" value={card.landing_pages_published} />
                <Mini icon={Video} label="Videos" value={card.video_projects} />
                <Mini icon={Newspaper} label="Berichte" value={card.research_reports} />
                <Mini icon={ImageIcon} label="Bilder" value={card.images} />
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {archivedCards.length > 0 && (
        <>
          <h2 className="text-sm font-semibold tracking-wider uppercase text-slate-400 mt-10 mb-3">Archiviert</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {archivedCards.map((c) => {
              const p = c.project;
              return (
                <Card key={p.id} className="bg-[#0f1623] border-white/5 text-slate-400" data-testid={`project-archived-${p.id}`}>
                  <CardContent className="p-4 flex items-center justify-between gap-3">
                    <div className="flex items-center gap-3 min-w-0">
                      <span className="h-9 w-9 rounded-lg grid place-items-center" style={{ background: (p.color || "#64748b") + "22", color: p.color }}>{p.emoji || "·"}</span>
                      <div className="min-w-0">
                        <div className="font-semibold truncate text-slate-200">{p.name}</div>
                        <Badge variant="outline" className="border-white/10 text-slate-500 mt-1 text-[10px]">archiviert</Badge>
                      </div>
                    </div>
                    <Button size="sm" variant="outline" className="border-white/10 bg-transparent text-slate-300" onClick={() => unarchive(p.id)} disabled={busy[p.id]} data-testid={`project-unarchive-${p.id}`}>Reaktivieren</Button>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}

function Stat({ label, value, icon: Icon, tint, testid }) {
  const tints = {
    emerald: "text-emerald-400 bg-emerald-500/10",
    indigo: "text-indigo-400 bg-indigo-500/10",
    amber: "text-amber-400 bg-amber-500/10",
    violet: "text-violet-400 bg-violet-500/10",
  };
  return (
    <Card className="bg-[#111827] border-white/5 text-slate-100" data-testid={testid}>
      <CardContent className="p-4 flex items-center justify-between">
        <div>
          <div className="text-xs uppercase tracking-wider text-slate-400">{label}</div>
          <div className="text-2xl font-extrabold text-white mt-1">{value}</div>
        </div>
        <div className={`h-9 w-9 rounded-lg grid place-items-center ${tints[tint]}`}><Icon className="h-4 w-4" /></div>
      </CardContent>
    </Card>
  );
}

function Mini({ icon: Icon, label, value }) {
  return (
    <div className="flex flex-col gap-0.5">
      <div className="flex items-center gap-1.5 text-[10px] uppercase text-slate-500">
        <Icon className="h-3 w-3" />{label}
      </div>
      <div className="text-sm font-bold text-white">{value}</div>
    </div>
  );
}
